import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils';

interface PatientInformationSectionProps {
  patientPII: any;
  isEditing: boolean;
  editedPatientData: any;
  handleEditClick: () => void;
  handleSaveEdit: () => void;
  handleFieldChange: (field: string, value: string) => void;
  translateValue: (field: string, value: string) => string;
  handleProfilePhotoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveProfilePhoto: () => void;
  isUploading: boolean;
}

export function PatientInformationSection({ 
  patientPII,
  isEditing,
  editedPatientData,
  handleEditClick,
  handleSaveEdit,
  handleFieldChange,
  translateValue,
  handleProfilePhotoUpload,
  handleRemoveProfilePhoto,
  isUploading
}: PatientInformationSectionProps) {
  const { t, i18n } = useTranslation();
  const profilePhotoUrl = editedPatientData?.profile_photo_url || patientPII?.profile_photo_url;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="tab-panel">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px', 
        gap: '12px' 
      }}>
        <h3 className="form-block-title" style={{ color: '#000000', margin: 0 }}>
          👤 {t('patientDetail.information')}
        </h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!isEditing ? (
            <button
              onClick={handleEditClick}
              className="edit-patient-btn"
              style={{
                background: '#007acc',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {t('patientDetail.editPatient')}
            </button>
          ) : (
            <>
              <button
                onClick={handleSaveEdit}
                className="save-edit-btn"
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('patientDetail.saveChanges')}
              </button>
            </>
          )}
        </div>
      </div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
        gap: '20px'
      }}>
        {/* Profile Photo - Full width at top */}
        <div className="form-group" style={{ marginBottom: '30px', gridColumn: '1 / -1' }}>
          <label style={{ color: '#000000', marginBottom: '12px', display: 'block' }}>
            {t('patientDetail.profilePhoto')}
          </label>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '20px',
            flexDirection: i18n.language === 'he' ? 'row-reverse' : 'row'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'visible',
              border: '3px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              flexShrink: 0,
              position: 'relative'
            }}>
              {profilePhotoUrl ? (
                <>
                  <img 
                    src={profilePhotoUrl} 
                    alt={t('patientDetail.profilePhoto')}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveProfilePhoto}
                    style={{
                      position: 'absolute',
                      top: i18n.language === 'he' ? '-8px' : '-8px',
                      right: i18n.language === 'he' ? '-8px' : '-8px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#dc3545',
                      color: 'white',
                      border: '2px solid white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      padding: 0,
                      lineHeight: 1
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#c82333';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#dc3545';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title={t('patientDetail.removeProfilePhoto')}
                  >
                    ×
                  </button>
                </>
              ) : (
                <div style={{ 
                  fontSize: '48px',
                  color: '#6c757d'
                }}>
                  👤
                </div>
              )}
            </div>
            {!profilePhotoUrl && (
              <div style={{ flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoUpload}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                  id="profile-photo-upload"
                />
                <button
                  type="button"
                  onClick={() => {
                    console.log('Upload Profile Photo button clicked');
                    fileInputRef.current?.click();
                  }}
                  disabled={isUploading}
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    background: isUploading ? '#6c757d' : '#007acc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'center',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading) {
                      e.currentTarget.style.background = '#005a9e';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isUploading) {
                      e.currentTarget.style.background = '#007acc';
                    }
                  }}
                >
                  {isUploading ? t('patientDetail.uploading') : t('patientDetail.uploadProfilePhoto')}
                </button>
              </div>
            )}
          </div>
        </div>
        {/* First Name */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.firstName')}</label>
          {isEditing ? (
            <input
              type="text"
              value={editedPatientData?.first_name || ''}
              onChange={(e) => handleFieldChange('first_name', e.target.value)}
              className="patient-edit-input"
              placeholder={t('intakeForm.enterFirstName')}
            />
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {patientPII.first_name || '—'}
            </div>
          )}
        </div>

        {/* Last Name */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.lastName')}</label>
          {isEditing ? (
            <input
              type="text"
              value={editedPatientData?.last_name || ''}
              onChange={(e) => handleFieldChange('last_name', e.target.value)}
              className="patient-edit-input"
              placeholder={t('intakeForm.enterLastName')}
            />
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {patientPII.last_name || '—'}
            </div>
          )}
        </div>

        {/* Date of Birth */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.dateOfBirth')}</label>
          {isEditing ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type="date"
                value={editedPatientData?.date_of_birth || ''}
                onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
                className="patient-edit-input"
                style={{
                  paddingRight: i18n.language === 'he' ? '12px' : '40px',
                  paddingLeft: i18n.language === 'he' ? '40px' : '12px'
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                  pointerEvents: 'none',
                  fontSize: '18px'
                }}
              >
                📅
              </span>
            </div>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {formatDate(patientPII.date_of_birth) || '—'}
            </div>
          )}
        </div>

        {/* Gender */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.gender')}</label>
          {isEditing ? (
            <select
              value={editedPatientData?.gender || ''}
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              className="patient-edit-input"
              style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                border: '1px solid #ced4da', 
                backgroundColor: '#ffffff', 
                color: '#000000', 
                fontSize: '14px' 
              }}
            >
              <option value="">{t('intakeForm.selectGender')}</option>
              <option value="male">{t('intakeForm.male')}</option>
              <option value="female">{t('intakeForm.female')}</option>
            </select>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {translateValue('gender', patientPII.gender) || '—'}
            </div>
          )}
        </div>

        {/* Education */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.education')}</label>
          {isEditing ? (
            <select
              value={editedPatientData?.education || ''}
              onChange={(e) => handleFieldChange('education', e.target.value)}
              className="patient-edit-input"
              style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                border: '1px solid #ced4da', 
                backgroundColor: '#ffffff', 
                color: '#000000', 
                fontSize: '14px' 
              }}
            >
              <option value="">{t('patientDetail.selectEducationLevel')}</option>
              <option value="elementary">{t('patientDetail.elementarySchool')}</option>
              <option value="high_school">{t('patientDetail.highSchool')}</option>
              <option value="college">{t('patientDetail.college')}</option>
              <option value="university">{t('patientDetail.university')}</option>
              <option value="graduate">{t('patientDetail.graduateDegree')}</option>
              <option value="other">{t('patientDetail.other')}</option>
            </select>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {translateValue('education', patientPII.education) || '—'}
            </div>
          )}
        </div>

        {/* Email */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.email')}</label>
          {isEditing ? (
            <input
              type="email"
              value={editedPatientData?.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              className="patient-edit-input"
              placeholder={t('intakeForm.enterEmail')}
            />
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {patientPII.email || '—'}
            </div>
          )}
        </div>

        {/* Phone */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.phone')}</label>
          {isEditing ? (
            <input
              type="tel"
              value={editedPatientData?.phone || ''}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              className="patient-edit-input"
              placeholder={t('intakeForm.enterPhone')}
            />
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {patientPII.phone || '—'}
            </div>
          )}
        </div>

        {/* Address */}
        <div className="form-group">
          <label style={{ color: '#000000' }}>{t('patientDetail.address')}</label>
          {isEditing ? (
            <textarea
              value={editedPatientData?.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              className="patient-edit-textarea"
              rows={4}
              placeholder={t('intakeForm.enterAddress')}
            />
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              color: '#000000',
              fontSize: '15px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'flex-start',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {patientPII.address || '—'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PatientInformationSection;

